import { Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { getItemsIds } from '../../../../utils/common'
import {
  Checkbox,
  CHECKBOX_SIZE,
  CheckboxGroupSkeleton,
} from '../../../Checkbox'
import { FormikField, getFormikCheckboxFieldValueProps } from '../../../Formik'
import {
  EXPORT_TABLE_FORM_FIELD_SELECT_ALL,
  INITIAL_VALUES,
} from '../TableExportModal.constants'
import { fillFormikValuesFromFieldList } from '../TableExportModal.utils'

import { FieldCheckbox } from './FieldCheckbox'

import * as styles from '../TableExportModal.module.css'

function FormFields({ loading, options }) {
  const { t } = useTranslation()
  const { resetForm, setFieldValue, setValues, values } = useFormikContext()

  // optionsIds and selected are useEffect dependencies below. Keep them memoized so
  // their references stay stable across `values` changes — otherwise resetForm re-runs
  // on every checkbox toggle and snaps all fields back to checked. The compiler does
  // not keep these stable on its own here, so don't drop the manual memo.
  const optionsIds = useMemo(() => getItemsIds(options, 'field_id'), [options])

  const selected = useMemo(
    () => optionsIds.filter(i => values[i]),
    [optionsIds, values]
  )

  useEffect(() => {
    resetForm({
      values: {
        ...INITIAL_VALUES,
        ...fillFormikValuesFromFieldList(optionsIds, true),
      },
    })
  }, [optionsIds])

  useEffect(() => {
    setFieldValue(
      EXPORT_TABLE_FORM_FIELD_SELECT_ALL,
      selected.length === options.length
    )
  }, [selected])

  function handleChangeSelectAll(value) {
    setValues(fillFormikValuesFromFieldList(optionsIds, value))
  }

  return loading ? (
    <CheckboxGroupSkeleton />
  ) : (
    <>
      <div className={styles.item} key={EXPORT_TABLE_FORM_FIELD_SELECT_ALL}>
        <FormikField
          Component={Checkbox}
          Field={Fragment}
          className={styles.checkbox}
          getValueProps={getFormikCheckboxFieldValueProps}
          label={t('Modal.ExportDataSelectAll')}
          name={EXPORT_TABLE_FORM_FIELD_SELECT_ALL}
          size={CHECKBOX_SIZE.small}
          onChange={handleChangeSelectAll}
        />
      </div>
      {options.map(field => (
        <FieldCheckbox
          allSelected={values[EXPORT_TABLE_FORM_FIELD_SELECT_ALL]}
          key={field.field_id}
          label={field.caption}
          name={field.field_id}
        />
      ))}
    </>
  )
}

export default FormFields
